import React from "react";
import { reportClientLog } from "./livekit/clientLogReporter";

/**
 * Catches React render/lifecycle errors and shows a recovery UI.
 * (Event-handler/async errors still use window error / unhandledrejection via clientLogReporter.)
 */
export default class GlobalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("[GlobalErrorBoundary]", error, info);
    reportClientLog("react_render_error", error?.message || String(error), {
      stack: error?.stack,
      context: { componentStack: info?.componentStack?.slice(0, 2000) },
    });
    this.setState({ info });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleTryAgain = () => {
    this.setState({ error: null, info: null });
  };

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    const isDev = process.env.NODE_ENV === "development";

    return (
      <div className="global-error-fallback" role="alert">
        <div className="global-error-fallback__card">
          <h1 className="global-error-fallback__title">Something went wrong</h1>
          <p className="global-error-fallback__text">
            The app hit an unexpected error. Try again, or reload the page. If you were in a meeting,
            you may need to rejoin.
          </p>
          <div className="global-error-fallback__actions">
            <button type="button" className="global-error-fallback__btn global-error-fallback__btn--primary" onClick={this.handleTryAgain}>
              Try again
            </button>
            <button type="button" className="global-error-fallback__btn" onClick={this.handleReload}>
              Reload page
            </button>
          </div>
          {isDev && (
            <details className="global-error-fallback__details">
              <summary>Technical details (development only)</summary>
              <pre className="global-error-fallback__pre">{error?.stack || String(error)}</pre>
              {info?.componentStack ? (
                <pre className="global-error-fallback__pre global-error-fallback__pre--muted">{info.componentStack}</pre>
              ) : null}
            </details>
          )}
        </div>
      </div>
    );
  }
}
